if (!customElements.get('entrando-set-configurator')) {
  class EntrandoSetConfigurator extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.data = JSON.parse(this.querySelector('[data-set-configurator-data]').textContent);
      this.copy = JSON.parse(this.querySelector('[data-set-configurator-copy]').textContent);
      if (!this.productInfo || !this.data.variants.length) return;
      this.onChange = this.handleChange.bind(this);
      this.productInfo.addEventListener('change', this.onChange, true);
      this.observer = new MutationObserver(() => this.connectPicker());
      this.observer.observe(this.productInfo, { childList: true, subtree: true });
      this.connectPicker();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.productInfo?.removeEventListener('change', this.onChange, true);
    }

    connectPicker() {
      const picker = this.productInfo.querySelector('variant-selects[data-primary]');
      if (!picker || picker === this.picker) return;
      this.picker = picker;
      this.enhanced = false;
      this.fields = Array.from(picker.querySelectorAll('.product-form__input'));
      if (this.fields.length !== this.data.optionNames.length) {
        this.hidden = true;
        return;
      }
      this.selection = this.readValues();
      const row = this.findVariant(this.selection);
      if (!row) {
        this.hidden = true;
        return;
      }
      this.createColorDetails();
      this.createHelp();
      this.renderSelection(row);
      this.enhanced = true;
      this.hidden = false;
    }

    readValues() {
      return this.fields.map((field) => field.querySelector('input:checked, select')?.value || '');
    }

    writeValues(values) {
      this.fields.forEach((field, index) => {
        field.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = input.value === values[index];
        });
        const select = field.querySelector('select');
        if (select) {
          select.value = values[index];
          Array.from(select.options).forEach((option) => {
            option.toggleAttribute('selected', option.selected);
          });
        }
      });
    }

    findVariant(values) {
      return this.data.variants.find((row) => row.options.every((value, index) => value === values[index]));
    }

    createColorDetails() {
      this.details = null;
      this.colorSummary = null;
      if (!this.data.colorIndexes.length) return;
      this.details = document.createElement('details');
      this.details.className = 'entrando-set-colors';
      this.details.open = this.colorsOpen || false;
      const summary = document.createElement('summary');
      const text = document.createElement('span');
      const title = document.createElement('strong');
      title.textContent = this.copy.adjustColors;
      this.colorSummary = document.createElement('span');
      this.colorSummary.className = 'entrando-set-colors__selection';
      text.append(title, this.colorSummary);
      summary.append(text);
      this.details.append(summary);
      this.fields[0].before(this.details);
      this.data.colorIndexes.forEach((index) => {
        const field = this.fields[index];
        this.details.append(field);
        field.querySelectorAll('input[type="radio"]').forEach((input) => {
          const label = Array.from(field.querySelectorAll('label')).find((item) => item.htmlFor === input.id);
          const row = this.data.variants.find((variant) => variant.options[index] === input.value);
          const component = row && this.data.components[row.components[index]];
          if (!label || !component) return;
          const image = document.createElement('img');
          image.src = this.imageUrl(component.image, 100);
          image.alt = '';
          image.width = 48;
          image.height = 40;
          image.loading = 'lazy';
          const name = document.createElement('span');
          name.textContent = input.value;
          label.replaceChildren(image, name);
          label.classList.add('entrando-set-swatch');
        });
      });
      this.details.addEventListener('toggle', () => { this.colorsOpen = this.details.open; });
    }

    createHelp() {
      this.help = document.createElement('div');
      this.help.className = 'entrando-set-compatibility';
      this.help.hidden = true;
      this.help.setAttribute('role', 'status');
      this.help.setAttribute('aria-live', 'polite');
      (this.details || this.fields[this.data.sizeIndex]).after(this.help);
    }

    handleChange(event) {
      if (!this.enhanced || !this.picker?.contains(event.target) || !event.target.matches('input[type="radio"], select')) return;
      const requested = this.readValues();
      if (requested.some((value, index) => !this.data.variants.some((row) => row.options[index] === value))) return;
      const row = this.findVariant(requested);
      if (row) {
        this.selection = requested;
        this.clearHelp();
        this.renderSelection(row);
        return;
      }
      event.stopImmediatePropagation();
      this.writeValues(this.selection);
      const fieldIndex = this.fields.indexOf(event.target.closest('.product-form__input'));
      const candidates = this.data.colorIndexes.includes(fieldIndex)
        ? this.data.variants.filter((variant) => this.data.colorIndexes.every((index) => variant.options[index] === requested[index]))
        : [];
      this.showHelp(requested, candidates);
    }

    showHelp(requested, candidates) {
      this.help.replaceChildren();
      const intro = document.createElement('p');
      const selectedColors = this.data.colorIndexes.map((index) => requested[index]).join(' / ');
      intro.textContent = candidates.length
        ? this.copy.compatibleSizes.replace('__COLORS__', selectedColors)
        : this.copy.chooseColors;
      this.help.append(intro);
      if (candidates.length) {
        const choices = document.createElement('div');
        choices.className = 'entrando-set-compatibility__choices';
        candidates.forEach((row) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = this.copy.acceptSize.replace('__SIZE__', row.options[this.data.sizeIndex]);
          button.addEventListener('click', () => this.acceptCombination(row));
          choices.append(button);
        });
        this.help.append(choices);
      }
      const dismiss = document.createElement('button');
      dismiss.type = 'button';
      dismiss.className = 'entrando-set-compatibility__dismiss';
      dismiss.textContent = this.copy.keepSelection;
      dismiss.addEventListener('click', () => this.clearHelp());
      this.help.append(dismiss);
      this.help.hidden = false;
      if (this.details) this.details.open = true;
    }

    clearHelp() {
      if (this.help) {
        this.help.hidden = true;
        this.help.replaceChildren();
      }
    }

    acceptCombination(row) {
      this.writeValues(row.options);
      const sizeField = this.fields[this.data.sizeIndex];
      const input = sizeField.querySelector('input:checked, select');
      if (input) input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    renderSelection(row) {
      const list = this.querySelector('[data-set-preview-items]');
      const fragment = document.createDocumentFragment();
      row.components.forEach((id, index) => {
        const component = this.data.components[id];
        const item = document.createElement('li');
        item.className = 'entrando-set-preview__item';
        const image = document.createElement('img');
        image.src = this.imageUrl(component.image, 240);
        image.alt = [this.copy.componentLabels[index], component.color, component.size].join(', ');
        image.width = 120;
        image.height = 82;
        image.loading = 'lazy';
        const title = document.createElement('strong');
        title.textContent = this.copy.componentLabels[index];
        const color = document.createElement('span');
        color.className = 'entrando-set-preview__color';
        color.textContent = component.color;
        const size = document.createElement('span');
        size.textContent = component.size;
        item.append(image, title, color, size);
        fragment.append(item);
      });
      list.replaceChildren(fragment);
      if (this.colorSummary) this.colorSummary.textContent = this.data.colorIndexes.map((index) => row.options[index]).join(' / ');
      this.dataset.variantId = row.id;
    }

    imageUrl(source, width) {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    }
  }
  customElements.define('entrando-set-configurator', EntrandoSetConfigurator);
}

